import Moment from 'moment';
import _ from 'lodash';

import { styled } from '@mui/material';
import { Modal } from '../../../components/modals/v2/helpers/v2.modal.helpers';
import { NameInsuredAttestationConfig } from '../quotes/NameInsuredAttestation';
import { ProductTypes } from '../../../types';
import { toUniversalUtcDate } from '../../../utils/date.utils';
import { moneyFormat, moneyFormat2Decimals } from '../../../utils/appUtils';
import { getConsolidatedP100CoverageKeys } from '../../workflows/quotes/prime-100/utils/coverages';

function getDateRange(fromDate: any, toDate: any, rangeMetric = 'month') {
  if (!fromDate || !toDate) {
    return null;
  }
  // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
  const range = Moment(toDate).diff(fromDate, rangeMetric);
  if (range > 1) {
    return `${range} ${rangeMetric}s`;
  }
  return `${range} ${rangeMetric}`;
}

function getRetroActivePeriod(retroactivePeriod: any) {
  if (!retroactivePeriod) {
    return null;
  }
  if (retroactivePeriod > 2) {
    return 'Full Prior Acts';
  }
  if (retroactivePeriod === 2) {
    return `${retroactivePeriod} Years`;
  }
  if (retroactivePeriod === 1) {
    return `${retroactivePeriod} Year`;
  }
}

const formatP100Policy = (rawPolicy: any) => {
  const filteredPolicy = {
    ..._.pick(rawPolicy, [
      'deductible',
      'totalPremium',
      'limit',
      'effectiveDate',
    ]),
  };
  return returnPolicyValues(filteredPolicy);
};

const formatP250Policy = (rawPolicy: any) => {
  const filteredPolicy = {
    ..._.pick(rawPolicy.initialRequestData, [
      'deductible',
      'limit',
      'effectiveDate',
    ]),
    ..._.pick(rawPolicy.policyResponse, ['totalPremium']),
  };
  return returnPolicyValues(filteredPolicy);
};

const returnPolicyValues = (filteredPolicy: any) => {
  return {
    ...filteredPolicy,
    formattedTotalPremium: filteredPolicy.totalPremium
      ? moneyFormat2Decimals(filteredPolicy.totalPremium)
      : null,
    formattedLimit: filteredPolicy.limit
      ? moneyFormat(filteredPolicy.limit)
      : null,
    formattedDeductible: filteredPolicy.deductible != null
      ? moneyFormat(filteredPolicy.deductible)
      : null,
    formattedEffectiveDate: filteredPolicy.effectiveDate
      ? Moment(filteredPolicy.effectiveDate)
          .utc()
          .format('MMM DD, YYYY hh:mm A')
      : null,
  };
};

export const formatQuoteFirmo = (rawQuote: any) => {
  return {
    ..._.pick(rawQuote, [
      'id',
      'customerStatus',
      'quoteNumber',
      'totalPremium',
      'agencyName',
      'agentEmail',
      'agentFirstName',
      'agentLastName',
      'agentPhone',
      'noKnownLossLetterReq',
      'created',
      'expiresOn',
      'isRenewal',
      'isMigration',
      // ISO 4217 currency code (MS-268) for this quote's premium/limit/deductible amounts.
      'currency',
    ]),
    ..._.pick(rawQuote.firmographicData, [
      'agencyBilled',
      'naicsCode',
      'name',
      'accountId',
      'isFranchise',
      'noOfEmployeesAll',
      'revenue',
      'percentInternationalSales',
      'numberOfPersonalRecords',
      'state',
      'mfaAuthentication',
      'mfaCloudDeployments',
      'mfaEmail',
      'mfaMissionCriticalSystems',
      'mfaOther',
      'mfaRemoteAccess',
      'changeInScope',
      'changeInScopeDetail',
      'incidentResponsePlan',
      'renewalChangeInAnswers',
      'secondaryIndustry',
    ]),
    ..._.pick(rawQuote.firmographicData.p250Details, [
      'backupFrequency',
      'backupFrequencyEncrypted',
      'backupFrequencyOther',
      'backupFrequencySeparate',
      'backupFrequencyTested',
    ]),
  };
};

export const formatP100Request = (rawQuote: any) => {
  const coverageKeys = getConsolidatedP100CoverageKeys();
  const globalDataKeys = [
    'product',
    'effectiveDate',
    'endDate',
    'waitingPeriod',
    'additionalInsureds',
  ];

  return {
    ..._.pick(rawQuote.initialRequestData, [
      ...coverageKeys,
      ...globalDataKeys,
    ]),
  };
};

export const formatP250Request = (rawQuote: any) => {
  return {
    ..._.pick(rawQuote.initial250RequestData, [
      'limit',
      'deductible',
      'effectiveDate',
      'isEndorsement',
      'endDate',
      'product',
      'coverages',
      'subjectivities',
      'surplusCompliance',
      'surplusCowbellInsurance',
      'tria',
      'mgaFee',
    ]),
  };
};

export const formatP100Quote = (rawQuote: any) => {
  const filteredQuote = {
    ...formatQuoteFirmo(rawQuote),
    ...formatP100Request(rawQuote),
  };

  return returnQuoteValues(filteredQuote);
};

const formatP250Quote = (rawQuote: any) => {
  const filteredQuote = {
    ...formatQuoteFirmo(rawQuote),
    ...formatP250Request(rawQuote),
  };

  return returnQuoteValues(filteredQuote);
};

const returnQuoteValues = (filteredQuote: any) => {
  return {
    ...filteredQuote,
    formattedTotalPremium: filteredQuote.totalPremium
      ? moneyFormat2Decimals(filteredQuote.totalPremium)
      : null,
    formattedLimit: filteredQuote.limit
      ? moneyFormat(filteredQuote.limit)
      : null,
    formattedDeductible: filteredQuote.deductible != null
      ? moneyFormat(filteredQuote.deductible)
      : null,
    formattedPolicyTerm: getDateRange(
      filteredQuote.effectiveDate,
      filteredQuote.endDate
    ),
    formattedRetroactivePeriod: getRetroActivePeriod(
      filteredQuote.retroactivePeriod
    ),
    formattedEffectiveDate: filteredQuote.effectiveDate
      ? Moment(filteredQuote.effectiveDate).utc().format('MMM DD, YYYY hh:mm A')
      : null,
    formattedEndDate: filteredQuote.endDate
      ? Moment(filteredQuote.endDate).utc().format('MMM DD, YYYY hh:mm A')
      : null,
    formattedCreated: filteredQuote.created
      ? Moment(filteredQuote.created).format('MMM DD, YYYY hh:mm A')
      : null,
    formattedExpiresOn: filteredQuote.expiresOn
      ? Moment(filteredQuote.expiresOn).format('MMM DD, YYYY hh:mm A')
      : null,
  };
};

export const formatAccount = (rawAccount: any) => {
  return {
    ..._.pick(rawAccount, [
      'isFranchise',
      'noOfEmployeesAll',
      'revenue',
      'percentInternationalSales',
      'numberOfPersonalRecords',
    ]),
  };
};

const formatCommonQuote = (rawQuote: any) => {
  return {
    ..._.pick(rawQuote, [
      'id',
      'quoteNumber',
      'noKnownLossLetterReq',
      'customerStatus',
      'created',
      'effectiveDate',
      'accountId',
    ]),
  };
};

export const formatP100AttestationQuote = (rawQuote: any) => {
  return {
    accountId: _.get(rawQuote, 'accountId'),
    ...formatCommonQuote(rawQuote),
    ..._.pick(rawQuote.initialRequestData, ['effectiveDate', 'product']),
  };
};

export const formatP250AttestationQuote = (rawQuote: any) => {
  return {
    ...formatCommonQuote(rawQuote),
    ..._.pick(rawQuote.initial250RequestData, ['effectiveDate', 'product']),
  };
};

export const formatPolicy = (policy: any, product: any) => {
  if (product === ProductTypes.p100) {
    return formatP100Policy(policy);
  }
  if (product === ProductTypes.p250) {
    return formatP250Policy(policy);
  }
};

export const formatQuote = (quote: any, product: any) => {
  if (product === ProductTypes.p100) {
    return formatP100Quote(quote);
  }
  if (product === ProductTypes.p250) {
    return formatP250Quote(quote);
  }
};

export const formatQuoteForAttestation = (quote: any, product: any) => {
  if (product === ProductTypes.p100) {
    return formatP100AttestationQuote(quote);
  }
  if (product === ProductTypes.p250) {
    return formatP250AttestationQuote(quote);
  }
};

export const getAttestLabelText = (
  effectiveDate: any,
  createdDate: any,
  product = ''
) => {
  return (
    <>
      I represent that between{' '}
      <ItalicText>{toUniversalUtcDate(effectiveDate)} (12:01 AM)</ItalicText>{' '}
      and<ItalicText>{toUniversalUtcDate(createdDate)}</ItalicText>, I am not
      currently aware of any accidents, facts or circumstances occurring during
      that time that may result in future claims covered under Cowbell Cyber
      Prime{product.replace(/[A-Z]/g, '')}.
    </>
  );
};

export const getPayloadAttestText = (
  effectiveDate: any,
  created: any,
  product = ''
) => {
  return `I represent that between ${toUniversalUtcDate(
    effectiveDate
  )} (12:01 AM) and ${toUniversalUtcDate(
    created
  )}, I am not currently aware of any accidents,
    facts or circumstances occurring during that time that may result in future claims covered under Cowbell Cyber Prime ${product.replace(
      /[A-Z]/g,
      ''
    )}.`;
};

const ItalicText = styled('i')(({ theme: { config } }) => ({
  fontWeight: config.weights.bold,
}));

export const handleSignatureInitiation = (quote: any, push: any) => {
  if (quote.product === ProductTypes.p100) {
    Modal.show(NameInsuredAttestationConfig, {
      data: { product: quote.product, quoteId: quote.id, quote },
    });
  } else if (quote.product === ProductTypes.p250) {
    push(`/customer/quotes/${quote.id}/sign/${quote.product}`);
  }
};
